<div class="tab-body">
  <div class="tab-head">
    <div>
      <h3>API Keys</h3>
      <p>Credentials this client uses to call our APIs. Secrets are hashed at rest and shown only once, at creation.</p>
    </div>
    <p-button label="Add API Key" icon="pi pi-plus" (onClick)="openAdd()" />
  </div>

  <!-- One-time secret reveal panel (shown right after Insert / Regenerate) -->
  @if (revealedSecret()) {
    <div class="reveal-panel">
      <div class="reveal-head">
        <i class="pi pi-exclamation-triangle"></i>
        <strong>Copy this secret now — it won't be shown again.</strong>
        <button type="button" class="reveal-close" (click)="dismissRevealed()" aria-label="Dismiss">
          <i class="pi pi-times"></i>
        </button>
      </div>
      @if (revealedKey()) {
        <div class="reveal-row">
          <span class="reveal-label">API Key</span>
          <code class="reveal-value">{{ revealedKey() }}</code>
          <p-button icon="pi pi-copy" [rounded]="true" [text]="true" size="small"
            pTooltip="Copy" (onClick)="copy(revealedKey())" />
        </div>
      }
      <div class="reveal-row">
        <span class="reveal-label">API Secret</span>
        <code class="reveal-value">{{ revealedSecret() }}</code>
        <p-button icon="pi pi-copy" [rounded]="true" [text]="true" size="small"
          pTooltip="Copy" (onClick)="copy(revealedSecret())" />
      </div>
    </div>
  }

  <p-table [value]="rows()" [loading]="loading()" dataKey="id" [rows]="10"
    [paginator]="rows().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
    <ng-template pTemplate="header">
      <tr>
        <th>Environment</th>
        <th>Scope</th>
        <th>API Key</th>
        <th>API Secret</th>
        <th>Allowed IPs</th>
        <th class="ta-r">Rate / min</th>
        <th>Expiry</th>
        <th class="ta-c">Active</th>
        <th class="ta-r">Actions</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-r>
      <tr>
        <td>
          <p-tag [value]="r.environment" [severity]="envSeverity(r.environment)" />
        </td>
        <td>
          @if (r.branchName) {
            <span class="name">{{ r.branchName }}</span>
            <p-tag value="Locked" severity="warn" pTooltip="This key is locked to this branch only" />
          } @else {
            <p-tag value="Account · all branches" severity="success"
              pTooltip="Spans all branches; narrow per request with ?branchCode= or the X-Branch-Code header" />
          }
        </td>
        <td>
          <div class="secret-cell">
            <code class="mono">{{ mask(r.apiKey) }}</code>
            @if (r.apiKey) {
              <p-button icon="pi pi-copy" [rounded]="true" [text]="true" size="small"
                severity="secondary" pTooltip="Copy" (onClick)="copy(r.apiKey)" />
            }
          </div>
        </td>
        <td>
          <span class="hint-muted"
            pTooltip="For security the secret is stored hashed and shown only once, at creation. Regenerate to get a new one.">
            <i class="pi pi-lock"></i> Shown once
          </span>
        </td>
        <td class="mono">{{ r.allowedIPs || 'Unrestricted' }}</td>
        <td class="ta-r">{{ r.rateLimitPerMinute ?? '—' }}</td>
        <td>{{ r.expiryDate ? (r.expiryDate | indDate) : 'No expiry' }}</td>
        <td class="ta-c">
          <span class="active-toggle" (click)="toggleActive(r)" pTooltip="Click to toggle">
            <p-tag [value]="r.isActive ? 'Active' : 'Inactive'"
              [severity]="r.isActive ? 'success' : 'secondary'" />
          </span>
        </td>
        <td class="ta-r actions">
          @if (r.publicEmbedKey) {
            <p-button icon="pi pi-code" [rounded]="true" [text]="true" severity="info"
              pTooltip="Embed enrollment widget (get snippet)" (onClick)="openEmbed(r)" />
          }
          <p-button icon="pi pi-refresh" [rounded]="true" [text]="true" severity="warn"
            pTooltip="Regenerate" (onClick)="regenerate(r)" />
          <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
            pTooltip="Edit" (onClick)="openEdit(r)" />
          <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
            pTooltip="Delete" (onClick)="remove(r)" />
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="9">
          <div class="state"><i class="pi pi-key"></i>
            <p>No API keys yet. Click "Add API Key" to issue one.</p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editing() ? 'Edit API Key' : 'Add API Key'" styleClass="menu-dialog"
  [style]="{ width: '560px' }" [breakpoints]="{ '560px': '94vw' }">
  <form [formGroup]="form" class="menu-form">
    <div class="field">
      <label>Scope</label>
      <p-select formControlName="branchId" [options]="branchOptions()" optionLabel="label"
        optionValue="value" placeholder="Account — all branches" appendTo="body"
        [filter]="branchOptions().length > 7" filterBy="label" />
      <small class="hint-muted">
        <strong>Account</strong> = full access to all branches (narrow per request with
        <code>?branchCode=</code> or the <code>X-Branch-Code</code> header). Pick a branch to
        <strong>lock</strong> this key to that branch only — it can never reach the others.
      </small>
    </div>

    <div class="field">
      <label>Environment <span class="req">*</span></label>
      @if (editing()) {
        <p-tag [value]="form.get('environment')?.value" [severity]="envSeverity(form.get('environment')?.value)" />
        <small class="hint-muted">
          Environment is fixed at creation. Create a new key to use a different environment.
        </small>
      } @else {
        <p-select formControlName="environment" [options]="environmentOptions()" optionLabel="label"
          optionValue="value" placeholder="Select environment" appendTo="body" />
        @if (form.get('environment')?.touched && form.get('environment')?.invalid) {
          <small class="err">Environment is required</small>
        }
        <small class="hint-muted">
          Keys are env-prefixed (e.g. <code>lapitch_live_…</code>) and issued automatically on save.
        </small>
      }
    </div>

    <div class="field">
      <label>Allowed IPs</label>
      <input pInputText type="text" formControlName="allowedIPs"
        placeholder="203.0.113.4, 10.0.0.0/24 (blank = unrestricted)" />
      <small class="hint-muted">Comma-separated IPs / CIDR ranges. Leave blank to allow any IP.</small>
    </div>

    <div class="field">
      <label>Embed widget — allowed website domains</label>
      <input pInputText type="text" formControlName="embedAllowedOrigins"
        placeholder="https://www.clientsite.com, https://clientsite.com (blank = any site)" />
      <small class="hint-muted">
        Site origins the drop-in enrollment widget may run from (comma-separated; blank = any site).
        The widget uses a <strong>public embed key</strong> that is safe to expose — the API secret never
        leaves your server. Get the copy-paste snippet from the <i class="pi pi-code"></i> button on the key row.
      </small>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Rate Limit / minute</label>
        <p-inputnumber formControlName="rateLimitPerMinute" [min]="0" [useGrouping]="false"
          placeholder="e.g. 120" />
      </div>
      <div class="field">
        <label>Expiry Date</label>
        <p-datepicker formControlName="expiryDate" dateFormat="dd M yy" [showIcon]="true"
          [iconDisplay]="'input'" [readonlyInput]="true" appendTo="body" placeholder="No expiry" />
      </div>
    </div>

    <label class="switch-row">
      <p-toggleSwitch formControlName="isActive" />
      <span>Active</span>
    </label>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
      <p-button [label]="saving() ? 'Saving…' : 'Save'" icon="pi pi-check" (onClick)="save()"
        [disabled]="saving()" [loading]="saving()" />
    </div>
  </form>
</p-dialog>

<!-- ===================== EMBED WIDGET SNIPPET ===================== -->
<p-dialog [visible]="showEmbed()" (visibleChange)="showEmbed.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [dismissableMask]="true" header="Embed enrollment widget" styleClass="menu-dialog"
  [style]="{ width: '660px' }" [breakpoints]="{ '660px': '94vw' }">
  @if (embedRow(); as k) {
    <div class="embed-body">
      <p class="hint-muted">
        Paste this on the client's website. Clicking any element with <code>data-arogya-enroll</code> opens a new
        tab and starts enrollment. The <strong>API secret is never used here</strong> — only the public embed
        key below reaches the browser, and it can only start an enrollment session.
      </p>

      <div class="reveal-row">
        <span class="reveal-label">Public key</span>
        <code class="reveal-value">{{ k.publicEmbedKey }}</code>
        <p-button icon="pi pi-copy" [rounded]="true" [text]="true" size="small"
          pTooltip="Copy" (onClick)="copy(k.publicEmbedKey)" />
      </div>
      <div class="reveal-row">
        <span class="reveal-label">Allowed sites</span>
        <code class="reveal-value">{{ k.embedAllowedOrigins || 'Any website (not restricted)' }}</code>
      </div>

      <label class="embed-label">Copy-paste snippet</label>
      <pre class="embed-snippet">{{ embedSnippet(k) }}</pre>

      <div class="modal-actions">
        <p-button label="Copy snippet" icon="pi pi-copy" (onClick)="copy(embedSnippet(k))" />
        <p-button label="Close" [outlined]="true" (onClick)="closeEmbed()" />
      </div>
    </div>
  }
</p-dialog>
